import { useCallback, useEffect, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { getMfaStatus } from '../api/mfa';

type MfaGateState = {
  status: 'loading' | 'required' | 'ok';
  /** null mientras no se sabe (sin sesión, cargando o error al consultar). */
  hasFactor: boolean | null;
};

/**
 * Indica si la sesión debe pasar por el código de la app autenticadora antes
 * de mostrar la plataforma. Se reevalúa con cada token nuevo (tras validar el
 * código la sesión sube a aal2 y cambia el access_token).
 */
const useMfaGate = (session: Session | null) => {
  const accessToken = session?.access_token;
  const userId = session?.user.id ?? null;
  const [checked, setChecked] = useState<
    MfaGateState & { userId: string | null }
  >({ status: 'loading', hasFactor: null, userId: null });
  const requestId = useRef(0);

  const check = useCallback(async () => {
    const id = ++requestId.current;
    if (!accessToken) {
      // Al cerrar sesión se olvida el resultado: si el mismo usuario vuelve a
      // entrar, se consulta de nuevo antes de mostrar la app.
      setChecked({ status: 'loading', hasFactor: null, userId: null });
      return;
    }
    try {
      const result = await getMfaStatus();
      if (id === requestId.current) setChecked({ ...result, userId });
    } catch (error) {
      // Si no se puede leer el nivel, no bloquear el acceso: hoy la
      // verificación es opcional y el backend aún no la exige.
      console.error('Error checking MFA status:', error);
      if (id === requestId.current) {
        setChecked({ status: 'ok', hasFactor: null, userId });
      }
    }
  }, [accessToken, userId]);

  useEffect(() => {
    check();
  }, [check]);

  // Un usuario recién logueado queda en 'loading' desde el primer render (no
  // recién en el efecto), así la app no alcanza a montarse con una sesión que
  // pide código. Un refresh del token del mismo usuario mantiene el estado.
  const state: MfaGateState = !accessToken
    ? { status: 'ok', hasFactor: null }
    : checked.userId !== userId
      ? { status: 'loading', hasFactor: null }
      : checked;

  return { ...state, recheck: check };
};

export default useMfaGate;
